import { hasAnyPermission } from "shared/imports/permissionFunctions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

// PAC permission constants
const PAC_PERMISSIONS = [DjangIO.app.models.PermissionType.pac_hybrid, DjangIO.app.models.PermissionType.qpac_v1]

/**
 * Checks if the current user has any PAC permissions (hybrid or v1).
 * @returns {boolean} True if user has PAC permissions, false otherwise
 */
export const hasAnyPacPermissions = () => hasAnyPermission(PAC_PERMISSIONS)

/**
 * Checks if the current user has PAC hybrid permissions.
 * @returns {boolean} True if user has pac_hybrid permission, false otherwise
 */
export const hasPacHybrid = () => hasAnyPermission([DjangIO.app.models.PermissionType.pac_hybrid])

/**
 * Checks if the current user can view PAC receipts based on their PAC version and permissions.
 *
 * This function implements the permission hierarchy for PAC 2.0 with backwards compatibility:
 * - PAC 2.0 users (pac_hybrid + ff_permissions_for_pac_2): Uses can_view_pac_2_receipts
 * - Legacy QPAC v1 users: Uses can_view_pac_receipts
 * - Feature flag disabled: Falls back to legacy can_view_pac_receipts behavior
 *
 * @returns {boolean} True if user can view PAC receipts, false otherwise
 */
export const canViewPacReceipts = () => {
    if (!hasAnyPacPermissions()) return false

    // Use PAC 2.0 permissions if feature flag is enabled and user has pac_hybrid
    return isFeatureEnabled("ff_permissions_for_pac_2") && hasPacHybrid()
        ? Userdata.can_view_pac_2_receipts
        : Userdata.can_view_pac_receipts
}

/**
 * Checks if the current user can view PAC-related grassroots actions.
 * @returns {boolean} True if user can view PAC grassroots actions, false otherwise
 */
export const canViewGrassrootsActionPacData = () => hasAnyPacPermissions() && canViewPacReceipts()

/**
 * Determines widget permission logic for transaction widgets.
 * This function encapsulates the complex permission logic that was previously duplicated
 * across ListWidget, NumberWidget, and VisualizationWidget components.
 *
 * @param {Object} qdt - The QuorumDataType object with a value property
 * @returns {Object} Object containing permission information:
 *   - isTransactionsWidget: boolean indicating if this is a transaction widget
 *   - hasTransactionPermission: boolean indicating if user can view this transaction widget
 *   - isForbidden: boolean indicating if the widget should be forbidden (convenience property)
 */
export const getWidgetTransactionPermissions = (qdt) => {
    // Legacy PAC 1.0 permission logic
    const canViewTransactions =
        !isFeatureEnabled("ff_pac_permissions_one") ||
        Userdata.can_view_pac_disbursements ||
        Userdata.can_view_pac_receipts

    // Check if this is a transaction widget (legacy or PAC Classic)
    const isTransactionsWidget =
        qdt?.value === DjangIO.app.models.QuorumDataType.transaction.value ||
        qdt?.value === DjangIO.app.models.QuorumDataType.pacc_pac_transaction.value

    // For PAC 2.0, use the unified permission helper that handles both PAC 1.0 and PAC 2.0 permissions
    const canViewPacTransactions = canViewPacReceipts()

    // Determine final permission: use PAC 2.0 logic if it's a PAC Classic transaction AND PAC 2.0 permissions are enabled, otherwise use legacy logic
    const hasTransactionPermission =
        qdt?.value === DjangIO.app.models.QuorumDataType.pacc_pac_transaction.value &&
        isFeatureEnabled("ff_permissions_for_pac_2")
            ? canViewPacTransactions
            : canViewTransactions

    return {
        isTransactionsWidget,
        hasTransactionPermission,
        isForbidden: isTransactionsWidget && !hasTransactionPermission,
    }
}
